import API from '../Api/api';

export const ACCOUNT_PATH = '/account';
export const PENDING_KYC_FLOW_KEY = 'pending_listing_after_kyc';

const KYC_GATE_TTL_MS = 60_000;
const kycGateCache = new Map(); // role -> { at, data, promise }

/** Check whether user can submit property/project for a listing role */
export const checkUserKycGate = async (role) => {
  if (!role) return { allowed: true };

  const key = String(role).toLowerCase();
  const cached = kycGateCache.get(key);
  if (cached?.data && Date.now() - cached.at < KYC_GATE_TTL_MS) {
    return cached.data;
  }
  if (cached?.promise) return cached.promise;

  const promise = (async () => {
    try {
      const res = await API.get('/user-kyc/check', { params: { role: key } });
      const data = res.data || { allowed: true };
      kycGateCache.set(key, { at: Date.now(), data, promise: null });
      return data;
    } catch {
      const data = { allowed: true };
      kycGateCache.set(key, { at: Date.now(), data, promise: null });
      return data;
    }
  })();

  kycGateCache.set(key, { at: Date.now(), data: cached?.data || null, promise });
  return promise;
};

/** Warm KYC gate results so role selection can advance without waiting. */
export const prefetchUserKycGates = (roles = ['owner', 'agent']) => {
  (Array.isArray(roles) ? roles : [roles]).filter(Boolean).forEach((role) => {
    checkUserKycGate(role).catch(() => {});
  });
};

export const getUserKycForRole = async (role) => {
  const res = await API.get('/user-kyc/my', { params: { role } });
  return res.data;
};

export const isUserKycRequiredError = (err) =>
  err?.response?.data?.code === 'USER_KYC_REQUIRED';

export const savePendingListingFlow = (flow) => {
  if (!flow) return;
  try {
    sessionStorage.setItem(PENDING_KYC_FLOW_KEY, JSON.stringify(flow));
  } catch {
    /* ignore storage errors */
  }
};

export const getPendingListingFlow = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_KYC_FLOW_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearPendingListingFlow = () => {
  try {
    sessionStorage.removeItem(PENDING_KYC_FLOW_KEY);
  } catch {
    /* ignore */
  }
};

export const buildListingUrlFromPending = (flow) => {
  if (!flow?.role) return null;
  if (flow.context === 'project') {
    return `/project/create?role=${encodeURIComponent(flow.role)}`;
  }
  if (flow.listtype) {
    return `/property/create?role=${encodeURIComponent(flow.role)}&listtype=${encodeURIComponent(flow.listtype)}`;
  }
  return null;
};

/** Navigate to profile account page with KYC tab open */
export const navigateToUserKyc = (navigate, { role, pendingFlow } = {}) => {
  if (pendingFlow) savePendingListingFlow(pendingFlow);
  navigate(ACCOUNT_PATH, {
    state: { activeView: 'kyc', kycRole: role || pendingFlow?.role || 'owner' },
  });
};

/** Resume property/project flow after KYC is approved */
export const resumePendingListingFlow = (navigate) => {
  const flow = getPendingListingFlow();
  if (!flow) return false;

  clearPendingListingFlow();
  const directUrl = buildListingUrlFromPending(flow);

  if (directUrl && flow.blockedAt === 'submit') {
    navigate(directUrl);
    return true;
  }

  if (flow.context === 'project' && flow.role) {
    navigate(`/project/create?role=${encodeURIComponent(flow.role)}`);
    return true;
  }

  if (flow.context === 'property' && flow.role && flow.listtype) {
    navigate(
      `/property/create?role=${encodeURIComponent(flow.role)}&listtype=${encodeURIComponent(flow.listtype)}`,
    );
    return true;
  }

  if (flow.context === 'property' && flow.role) {
    navigate(`/role?context=property`, { state: { resumeRole: flow.role } });
    return true;
  }

  navigate(`/role?context=${flow.context || 'property'}`);
  return true;
};

/** Show toast and navigate to profile KYC tab */
export const handleUserKycApiError = (err, { toast, navigate, pendingFlow } = {}) => {
  if (!isUserKycRequiredError(err)) return false;
  const message =
    err.response?.data?.error ||
    'KYC verification is required before submitting a listing.';
  toast?.error(message);
  navigateToUserKyc(navigate, {
    role: pendingFlow?.role,
    pendingFlow: pendingFlow || undefined,
  });
  return true;
};
